import { Component, inject, computed, effect, signal , ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TourService } from '@core/services/tour.service';

/**
 * Tour overlay component
 * Displays guided tour steps with spotlight effect on target elements
 */
@Component({
  selector: 'app-tour-overlay',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatProgressBarModule],
  template: `
    @if (tourService.isActive()) {
      <!-- Backdrop -->
      <div class="tour-backdrop" (click)="onBackdropClick()"></div>

      <!-- Spotlight (highlights target element) -->
      @if (spotlightStyle() && hasTarget()) {
        <div class="tour-spotlight" [style]="spotlightStyle()"></div>
      }

      <!-- Tour card -->
      @if (step()) {
        <div 
          class="tour-card" 
          [ngStyle]="cardStyle()"
          [class.position-top]="hasTarget() && step()!.position === 'top'"
          [class.position-bottom]="hasTarget() && step()!.position === 'bottom'"
          [class.position-left]="hasTarget() && step()!.position === 'left'"
          [class.position-right]="hasTarget() && step()!.position === 'right'"
          [class.position-center]="!hasTarget()">
          <!-- Progress bar -->
          <mat-progress-bar 
            mode="determinate" 
            [value]="tourService.progress()"
            class="tour-progress">
          </mat-progress-bar>

          <!-- Content -->
          <div class="tour-content">
            <div class="tour-header">
              <h3 class="tour-title">{{ step()!.title }}</h3>
              <button 
                mat-icon-button 
                (click)="tourService.skipTour()"
                aria-label="Close tour"
                class="tour-close">
                <mat-icon>close</mat-icon>
              </button>
            </div>

            <p class="tour-text">{{ step()!.content }}</p>

            <!-- Step counter -->
            <div class="tour-counter">
              Step {{ tourService.currentStepIndex() + 1 }} of {{ totalSteps() }}
            </div>
          </div>

          <!-- Actions -->
          <div class="tour-actions">
            <button 
              mat-button 
              (click)="tourService.skipTour()"
              class="tour-skip">
              Skip Tour
            </button>

            <div class="tour-navigation">
              <button 
                mat-button 
                (click)="tourService.previousStep()"
                [disabled]="tourService.isFirstStep()"
                aria-label="Previous step">
                <mat-icon>arrow_back</mat-icon>
                Previous
              </button>

              @if (tourService.isLastStep()) {
                <button 
                  mat-raised-button 
                  color="primary"
                  (click)="tourService.completeTour()"
                  aria-label="Finish tour">
                  Finish
                  <mat-icon>check</mat-icon>
                </button>
              } @else {
                <button 
                  mat-raised-button 
                  color="primary"
                  (click)="tourService.nextStep()"
                  aria-label="Next step">
                  Next
                  <mat-icon>arrow_forward</mat-icon>
                </button>
              }
            </div>
          </div>
        </div>
      }
    }
  `,
  styles: [`
    .tour-backdrop {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.7);
      z-index: 9998;
      animation: fadeIn 0.3s ease-in-out;
    }

    .tour-spotlight {
      position: fixed;
      background: transparent;
      border: 3px solid var(--wm-blue-action);
      border-radius: var(--wm-radius-surface);
      box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.7);
      z-index: 9999;
      pointer-events: none;
      transition: all 0.3s ease-in-out;
    }

    .tour-card {
      position: fixed;
      background: var(--wm-surface);
      border-radius: var(--wm-radius-surface);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
      max-width: 400px;
      width: 90%;
      max-height: 80vh;
      overflow-y: auto;
      z-index: 10000;
      animation: slideIn 0.3s ease-in-out;
    }

    .tour-progress {
      border-radius: var(--wm-radius-surface) 12px 0 0;
      height: 4px;
    }

    .tour-content {
      padding: 24px;
    }

    .tour-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 12px;
    }

    .tour-title {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      color: var(--wm-ink);
      flex: 1;
    }

    .tour-close {
      margin-top: -8px;
      margin-right: -8px;
    }

    .tour-text {
      margin: 0 0 16px;
      font-size: 15px;
      line-height: 1.6;
      color: var(--wm-ink-3);
    }

    .tour-counter {
      font-size: 13px;
      color: var(--wm-muted);
      font-weight: 500;
    }

    .tour-actions {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 24px;
      border-top: 1px solid var(--wm-divider);
    }

    .tour-skip {
      color: var(--wm-muted);
    }

    .tour-navigation {
      display: flex;
      gap: 8px;
    }

    .tour-navigation button {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    /* Positioning classes */
    .position-center {
      transform: translate(-50%, -50%);
    }

    .position-top {
      transform: translateX(-50%) translateY(calc(-100% - 24px));
    }

    .position-bottom {
      transform: translateX(-50%) translateY(24px);
    }

    .position-left {
      transform: translateX(calc(-100% - 24px)) translateY(-50%);
    }

    .position-right {
      transform: translateX(24px) translateY(-50%);
    }

    /* Animations */
    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    @keyframes slideIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    /* Responsive */
    @media (max-width: 640px) {
      .tour-card {
        max-width: calc(100% - 32px);
        left: 16px !important;
        right: 16px !important;
        bottom: 16px !important;
        top: auto !important;
        transform: none !important;
      }

      .position-top,
      .position-bottom,
      .position-left,
      .position-right {
        transform: none !important;
      }
    }
  `]
})
export class TourOverlayComponent {
  tourService = inject(TourService);

  step = this.tourService.currentStep;
  totalSteps = computed(() => this.tourService.currentTour()?.steps.length || 0);

  // Position recalculation trigger - increment to force recomputation
  private positionTrigger = signal(0);

  // Writable signals updated by effect() to avoid DOM side-effects inside computed()
  private hasTargetSig = signal(false);
  private spotlightStyleSig = signal<Record<string, string> | null>(null);
  private cardStyleSig = signal<Record<string, string>>({ top: '50%', left: '50%' });

  readonly hasTarget = this.hasTargetSig.asReadonly();
  readonly spotlightStyle = this.spotlightStyleSig.asReadonly();
  readonly cardStyle = this.cardStyleSig.asReadonly();

  constructor() {
    // Update writable position signals via effect() — DOM queries are a side-effect
    // and must not live inside computed()
    effect(() => {
      const step = this.step();
      this.positionTrigger(); // Track as dependency so resize/scroll forces recalculation

      if (!step) {
        this.hasTargetSig.set(false);
        this.spotlightStyleSig.set(null);
        this.cardStyleSig.set({ top: '50%', left: '50%' });
        return;
      }

      const element = document.querySelector(step.target);
      this.hasTargetSig.set(!!element);

      if (!element) {
        this.spotlightStyleSig.set(null);
        this.cardStyleSig.set({ top: '50%', left: '50%' });
        return;
      }

      const rect = element.getBoundingClientRect();

      this.spotlightStyleSig.set({
        top: `${rect.top - 8}px`,
        left: `${rect.left - 8}px`,
        width: `${rect.width + 16}px`,
        height: `${rect.height + 16}px`
      });

      let top = 0;
      let left = rect.left + rect.width / 2;
      switch (step.position) {
        case 'top':
          top = rect.top;
          break;
        case 'bottom':
          top = rect.bottom;
          break;
        case 'left':
          top = rect.top + rect.height / 2;
          left = rect.left;
          break;
        case 'right':
          top = rect.top + rect.height / 2;
          left = rect.right;
          break;
        default:
          top = rect.top + rect.height / 2;
      }
      this.cardStyleSig.set({ top: `${top}px`, left: `${left}px` });
    }, { allowSignalWrites: true });

    // Re-calculate positions on window resize and scroll
    effect(() => {
      const isActive = this.tourService.isActive();
      const currentStep = this.step(); // Track step changes
      
      if (isActive) {
        // Force position recalculation when step changes
        this.positionTrigger.update(v => v + 1);
        
        window.addEventListener('resize', this.updatePositions);
        window.addEventListener('scroll', this.updatePositions, true);
        
        // Initial position calculation with retry for DOM readiness
        this.retryPositionCalculation();
      } else {
        window.removeEventListener('resize', this.updatePositions);
        window.removeEventListener('scroll', this.updatePositions, true);
      }
    });
  }

  private retryPositionCalculation(attempts = 0) {
    const MAX_ATTEMPTS = 10;
    const RETRY_DELAY = 100;

    if (attempts >= MAX_ATTEMPTS) return;

    // Check if target exists
    const step = this.step();
    if (step) {
      const element = document.querySelector(step.target);
      if (!element && attempts < MAX_ATTEMPTS) {
        // Retry after a delay
        setTimeout(() => this.retryPositionCalculation(attempts + 1), RETRY_DELAY);
      } else if (element) {
        // Element found, trigger position update
        this.positionTrigger.update(v => v + 1);
      }
    }
  }

  private updatePositions = () => {
    // Increment trigger to force re-computation of positions
    this.positionTrigger.update(v => v + 1);
  };

  onBackdropClick() {
    // Don't close tour on backdrop click, user must use controls
    // This prevents accidental tour closure
  }
}
